import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useState } from "react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Button } from "@/components/ui/Button";
import { FormCard } from "@/components/FormCard";
import { FormState, useAuthStore } from "@/stores/authStore";
import FederatedAuth from "./FederatedAuth";
import { signUpSchema } from "@/model/authSchema";
import { signUp } from "aws-amplify/auth";
import { PasswordRequirements } from "@/components/PasswordRequirements";
import BackButton from "@/components/ui/BackButton";

type TSignUpSchema = z.infer<typeof signUpSchema>;

type SignUpFormProps = {
  handleBack?: () => void;
};

export default function SignUpForm({ handleBack }: SignUpFormProps) {
  const { setFormState, data, setData } = useAuthStore();
  const [isPasswordPopoverOpen, setIsPasswordPopoverOpen] = useState(false);
  const form = useForm<TSignUpSchema>({
    resolver: zodResolver(signUpSchema),
    // mode: "all",
    defaultValues: {
      email: data.email || "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: TSignUpSchema) => {
    const { email, password } = data;

    try {
      const { nextStep } = await signUp({
        username: email,
        password: password,
        options: {
          userAttributes: {
            email,
          },
          autoSignIn: {
            authFlowType: "USER_SRP_AUTH",
          },
        },
      });

      // if (nextStep === "")

      setData({ email, password });
      setFormState(FormState.VERIFICATION);
    } catch (error: any) {
      const errorType = error.code || error.name || error.__type;
      if (errorType === "UsernameExistsException") {
        form.setError("email", {
          type: "manual",
          message: "Email already exists",
        });
      } else if (errorType === "UserNotConfirmedException") {
        setFormState(FormState.VERIFICATION);
      } else {
        form.setError("email", {
          type: "manual",
          message: "Something went wrong",
        });
      }
    }
  };

  return (
    <>
      <FormCard
        form={form}
        onSubmit={onSubmit}
        title="Register"
        description="Used for secure order verification"
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input placeholder="you@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="relative">
              <FormLabel>Password</FormLabel>
              <FormControl>
                <PasswordInput
                  {...field}
                  placeholder="********"
                  onFocus={() => setIsPasswordPopoverOpen(true)}
                  onBlur={() => setIsPasswordPopoverOpen(false)}
                />
              </FormControl>
              <FormMessage className="whitespace-pre-line" />
              {isPasswordPopoverOpen && (
                <PasswordRequirements password={form.watch("password")} />
              )}
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Confirm Password</FormLabel>
              <FormControl>
                <PasswordInput placeholder="********" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {handleBack ? (
          <div className="flex gap-3 justify-start">
            <BackButton handleBack={handleBack} className="flex-1" buttonClassName="w-full justify-start" />
            <Button
              type="submit"
              className="flex-1"
              disabled={form.formState.isSubmitting}
            >
              Continue
            </Button>
          </div>
        ) : (
          <Button
            type="submit"
            className="w-full"
            disabled={form.formState.isSubmitting}
          >
            Continue
          </Button>
        )}
        <p className="text-xs opacity-70">
          By logging in, you agree to SolarCloud's Privacy Policy and Terms and Conditions which includes how SolarCloud may send you marketing emails and messages, and how you can opt out or unsubscribe from these messages.
        </p>
        <FederatedAuth prefix="Register" />
        <div className="mb-10 text-center text-lg">
          <Button
            onClick={() => setFormState(FormState.SIGNIN)}
            type="button"
            className="mt-3 w-full text-black"
          >
            Already an existing customer? <strong>Sign in</strong>
          </Button>
        </div>
      </FormCard>

    </>
  );
}
